<template>
  <div class="performance-header" data-testid="performance-header">
    <div class="performance-header__main">
      <div
        v-if="!hasError || (hasError && hasFilter)"
        class="performance-header__main__filters"
        data-testid="performance-filters"
      >
        <FilterSelect
          class="performance-header__main__filter"
          label="Período"
          :value="activePeriodFilter.value"
          :options="periodFilters"
          :default-caption-color="NebraskaColors.themePrimary"
          :disabled="isLoading"
          use-option-label
          render-menu-with-portal
          @change="emitPeriodFilterChange"
        />
        <FilterSelect
          class="performance-header__main__filter"
          label="Benchmark"
          :value="activeBenchmarkFilter.value"
          :options="benchmarkFilters"
          :default-caption-color="NebraskaColors.elementSecondary"
          :disabled="isLoading"
          use-option-label
          render-menu-with-portal
          @change="emitBenchmarkFilterChange"
        />
        <SpinnerLoader
          v-if="isLoading"
          :size="16"
          :color="NebraskaColors.elementPlaceholder"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { FilterSelect, SpinnerLoader } from '@warrenbrasil/nebraska-web';

import { PortfolioChartFilter } from '@/modules/wealth/services/portfolio';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface Props {
  isLoading: boolean;
  hasError: boolean;
  periodFilters: PortfolioChartFilter[];
  benchmarkFilters: PortfolioChartFilter[];
}
const props = defineProps<Props>();

interface Emits {
  (e: 'on-period-filter-change', value: string): void;
  (e: 'on-benchmark-filter-change', value: string): void;
}
const emit = defineEmits<Emits>();

const activePeriodFilter = computed(() => {
  const filter = props.periodFilters?.find(filter => filter.active);
  return {
    value: filter?.key || '',
    label: filter?.label || '-'
  };
});

const activeBenchmarkFilter = computed(() => {
  const filter = props.benchmarkFilters?.find(filter => filter.active);
  return {
    value: filter?.key || '',
    label: filter?.label || '-'
  };
});

const hasFilter = computed(() => {
  return props.benchmarkFilters?.length > 0 || props.periodFilters?.length > 0;
});

const emitPeriodFilterChange = (value: string) => {
  emit('on-period-filter-change', value);
};

const emitBenchmarkFilterChange = (value: string) => {
  emit('on-benchmark-filter-change', value);
};
</script>

<style lang="less" scoped>
.performance-header {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &__main {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
    margin-top: @size-spacing-x500;

    .to-sm({
      flex-direction: column;
      align-items: flex-start;
    });

    &__filters {
      display: flex;
      align-items: center;

      .to-sm({
        flex-direction: row-reverse;
        margin-top: @size-spacing-x450;
      });
    }

    &__filter,
    &__filter--loader {
      margin-right: @size-spacing-x300;

      .to-sm({
        margin-right: @size-spacing-x300;;
        margin-left: 0;
      });
    }

    &__filter--loader {
      width: 100px;
      height: 32px;
      border-radius: @size-radius-max;
    }
  }
}
</style>
